import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { useGameStore } from '../../store/gameStore';

// Theme-specific styling - extracted to module scope for performance
const getThemeStyles = (theme) => {
  switch (theme) {
    case 'noir':
      return 'bg-slate-900 border-amber-900/50 text-amber-100';
    case '90s_nyc':
      return 'bg-zinc-900 border-orange-500/50 text-white';
    case 'miami_vice':
      return 'bg-purple-950 border-pink-500/50 text-white';
    default:
      return 'bg-slate-800 border-gray-600 text-white';
  }
};

export const GameTooltip = ({
  children,
  content,
  side = 'top',
  align = 'center',
  delayDuration = 300,
}) => {
  const tooltipsEnabled = useGameStore((state) => state.settings?.tooltipsEnabled ?? true);
  const selectedTheme = useGameStore((state) => state.selectedTheme);

  // If tooltips disabled, just render children
  if (!tooltipsEnabled) {
    return <>{children}</>;
  }

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className={`
            border px-3 py-2 rounded-md shadow-lg z-50
            ${getThemeStyles(selectedTheme)}
          `}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Suspect-specific tooltip
export const SuspectTooltip = ({ suspect, children }) => {
  const content = (
    <div className="max-w-xs">
      <p className="font-bold text-sm">{suspect.name}</p>
      <p className="text-xs opacity-80">{suspect.occupation}</p>
      {suspect.relationship_to_victim && (
        <p className="text-xs mt-1 opacity-70">
          {suspect.relationship_to_victim}
        </p>
      )}
    </div>
  );

  return (
    <GameTooltip content={content} side="right">
      {children}
    </GameTooltip>
  );
};

// Clue-specific tooltip
export const ClueTooltip = ({ clue, children }) => {
  const content = (
    <div className="max-w-xs">
      <p className="font-bold text-sm">{clue.name}</p>
      <p className="text-xs opacity-80 capitalize">{clue.type}</p>
      <p className="text-xs mt-1 line-clamp-2">
        {clue.description}
      </p>
    </div>
  );

  return (
    <GameTooltip content={content} side="right">
      {children}
    </GameTooltip>
  );
};

// Location-specific tooltip
export const LocationTooltip = ({ location, children }) => {
  const content = (
    <div className="max-w-xs">
      <p className="font-bold text-sm">{location.name}</p>
      <p className="text-xs mt-1 line-clamp-2 opacity-80">
        {location.description}
      </p>
    </div>
  );

  return (
    <GameTooltip content={content} side="right">
      {children}
    </GameTooltip>
  );
};

// Document-specific tooltip
export const DocumentTooltip = ({ document, children }) => {
  const content = (
    <div className="max-w-xs">
      <p className="font-bold text-sm">{document.title}</p>
      <p className="text-xs opacity-80 capitalize">{document.type}</p>
    </div>
  );

  return (
    <GameTooltip content={content} side="right">
      {children}
    </GameTooltip>
  );
};
